import { useState } from "react";
import { useNavigate, useLocation, useSearchParams, Link } from "react-router-dom";
import { PasswordInput } from "../components/Inputs";
import { useLoaderStore } from "../context";
import { useAlert, useApi } from "../hooks";
import { API } from "../utils/constants";

/**
 * ResetPassword — Shared "set a password via emailed token" page (SPR-008 FE-5).
 *
 * Serves BOTH routes (CON-002 §1, SPR-008 §2 — one token path):
 *   - /reset-password?token=…  (forgot-password reset)
 *   - /set-password?token=…    (invite acceptance)
 * The two differ only in copy; both POST /api/auth/reset-password
 * { token, newPassword }.
 *
 * Flow:
 * - new + confirm password (confirm must match new, client-side).
 * - On success → navigate to /login.
 * - 400 (invalid/expired/used token, or weak password) handled gracefully.
 * - Missing token → shows an invalid-link state.
 */
export default function ResetPassword() {
  const alert = useAlert();
  const { post } = useApi();
  const { setShowLoader } = useLoaderStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const token = searchParams.get("token") ?? "";
  // Invite (set-password) vs. forgot reset (reset-password) — copy only.
  const isInvite = location.pathname.startsWith("/set-password");

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const validate = () => {
    if (!newPassword.trim() || newPassword.trim().length < 8) {
      alert("Validation error", "Password must be at least 8 characters.", "warning");
      return false;
    }
    if (newPassword !== confirmPassword) {
      alert("Validation error", "Passwords do not match.", "warning");
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setShowLoader(true);
    try {
      await post(API.AUTH.RESET_PASSWORD, { token, newPassword });
      alert("Password set", "Your password has been set. Please sign in.", "success");
      navigate("/login");
    } catch (err) {
      if (err?.status === 400) {
        alert(
          "Link invalid",
          err?.data?.detail || "This link is invalid or has expired. Please request a new one.",
          "error"
        );
      } else {
        alert("Error", err?.data?.detail || "Failed to set your password.", "error");
      }
    } finally {
      setShowLoader(false);
    }
  };

  const heading = isInvite ? "Set Your Password" : "Reset Password";
  const cardTitle = isInvite ? "WELCOME — SET PASSWORD" : "CHOOSE A NEW PASSWORD";
  const intro = isInvite
    ? "Set a password to activate your account."
    : "Enter a new password for your account.";

  return (
    <div
      className="min-h-screen bg-cream flex items-center justify-center px-4"
      data-testid="reset-password-page"
    >
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <h1 className="font-display text-4xl text-charcoal leading-tight">{heading}</h1>
          <p className="font-body text-charcoal/60 mt-2 text-sm">Admin Portal</p>
        </div>

        <div className="bg-white border border-aluminum/30 rounded-sm shadow-sm p-8 space-y-5">
          {!token ? (
            <div className="space-y-4" data-testid="reset-password-missing-token">
              <h2 className="font-heading text-xl text-charcoal tracking-wider">INVALID LINK</h2>
              <p className="font-body text-charcoal/70 text-sm">
                This link is missing or invalid. Please request a new one.
              </p>
              <Link
                to="/forgot-password"
                className="block text-center w-full bg-teal text-white font-heading tracking-widest text-lg py-2.5 rounded-sm hover:bg-teal/80 transition-all"
                data-testid="request-new-link"
              >
                REQUEST A NEW LINK
              </Link>
            </div>
          ) : (
            <>
              <h2 className="font-heading text-xl text-charcoal tracking-wider">{cardTitle}</h2>
              <p className="font-body text-charcoal/60 text-sm">{intro}</p>

              <div>
                <PasswordInput
                  label="New Password"
                  name="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  showInfo
                  data-testid="new-password-input"
                />
              </div>

              <div>
                <PasswordInput
                  label="Confirm Password"
                  name="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleSubmit(); }}
                  data-testid="confirm-password-input"
                />
              </div>

              <button
                onClick={handleSubmit}
                className="w-full bg-teal text-white font-heading tracking-widest text-lg py-2.5 rounded-sm hover:bg-teal/80 active:scale-95 transition-all"
                data-testid="reset-password-button"
              >
                {isInvite ? "ACTIVATE ACCOUNT" : "SET PASSWORD"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
